import { useMarkerDetailsModal } from '@hooks/modals/useMarkerDetailsModal';
import { useGetFieldVisitsSteps } from '@hooks/useGetFieldVisitsSteps';
import { FieldVisitStep, Question, QUESTION_TYPE, STEP_STATUS } from '@mytypes/protocol';
import { setPreviousVisitsSelection } from '@state-mgmt/slices/visitSlice';
import { RootState } from '@state-mgmt/store';
import { createGenericColorMap } from '@utils/createGenericColorMap';
import { useEffect, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

export type PreviousVisitMarker = {
  characterization_id: string;
  color: string;
  latitude?: number | undefined;
  longitude?: number | undefined;
  value: any;
  valueType: string;
  label: string;
};

export default function usePreviousVisitMarkers({
  visits,
  stepId,
  trialId,
  fieldId,
  protocolId,
  loadedFromOfflineData = false
}: {
  visits: FieldVisitStep[] | undefined;
  stepId: string | undefined | null;
  trialId: string;
  fieldId: string;
  protocolId: string;
  loadedFromOfflineData?: boolean | null | undefined;
}) {
  const dispatch = useDispatch();
  const { previousVisitsSelection, historicalVisitsTrialId, historicalVisitsProtocolId } = useSelector((state: RootState) => state.visit);
  const { showBoundary } = useErrorBoundary();

  const [getFieldVisitsStepsState, getFieldVisitsSteps] = useGetFieldVisitsSteps();
  console.log('visits', visits);
  const currentlySelectedTrialsVisits = useMemo(() => {
    if (!trialId) return null;
    if (!historicalVisitsTrialId && !loadedFromOfflineData) return visits;
    if (trialId === historicalVisitsTrialId && protocolId === historicalVisitsProtocolId && !loadedFromOfflineData) return visits;
    return getFieldVisitsStepsState.isLoading ? null : getFieldVisitsStepsState.data;
  }, [
    trialId,
    historicalVisitsTrialId,
    getFieldVisitsStepsState.data,
    getFieldVisitsStepsState.isLoading,
    visits,
    historicalVisitsProtocolId,
    protocolId,
    loadedFromOfflineData
  ]);

  useEffect(() => {
    if (!trialId || !historicalVisitsTrialId || !historicalVisitsProtocolId) return;
    if (trialId !== historicalVisitsTrialId || protocolId !== historicalVisitsProtocolId || loadedFromOfflineData) {
      getFieldVisitsSteps(historicalVisitsTrialId, historicalVisitsProtocolId, fieldId, undefined, true);
    }
  }, [trialId, historicalVisitsTrialId, getFieldVisitsSteps, historicalVisitsProtocolId, fieldId, protocolId, loadedFromOfflineData]);

  const { displayModal: displayMarkerDetailsModal } = useMarkerDetailsModal();

  const previousVisits = useMemo(() => {
    if (!currentlySelectedTrialsVisits) return [];
    return currentlySelectedTrialsVisits?.filter(step => (!stepId || step.stepId !== stepId) && step.mainActionCompleted === STEP_STATUS.COMPLETE) || [];
  }, [currentlySelectedTrialsVisits, stepId]);

  useEffect(() => {
    const { getColorById } = createGenericColorMap(previousVisits, visit => visit.stepId);
    dispatch(
      setPreviousVisitsSelection(
        previousVisits.map(visit => ({
          id: visit.stepId,
          label: visit.label,
          completedAt: visit.completedAt,
          selected: false,
          color: getColorById(visit.stepId)
        }))
      )
    );
  }, [previousVisits]);

  const previousVisitsMarkers = useMemo(() => {
    let visits: (FieldVisitStep & { color: string })[] = [];

    previousVisits.forEach(previousVisit => {
      const isSelected = previousVisitsSelection.find(pvs => pvs.id === previousVisit.stepId && pvs.selected);
      if (isSelected) {
        visits = [...visits, { ...previousVisit, color: isSelected.color }];
      }
    });

    if (!visits.length) {
      return [];
    }

    let previousMarker: PreviousVisitMarker[] = [];
    visits.forEach(visit => {
      const reduced = visit.pages.reduce((acc: PreviousVisitMarker[], page) => {
        let validQuestions: Question[] = [];
        page.questions.forEach(question => {
          if (question.valueType === QUESTION_TYPE.UPLOAD) {
            const uploadsValues = question.value || [];
            const uploads = uploadsValues.map((upload: any) => ({ ...question, value: upload.url, latitude: upload.latitude, longitude: upload.longitude }));
            validQuestions = [...validQuestions, ...uploads];
          } else {
            if (question.value !== undefined && question.latitude !== undefined && question.longitude !== undefined) {
              validQuestions = [...validQuestions, question];
            }
          }
        });

        validQuestions.forEach(question => {
          acc.push({
            characterization_id: question.characterization_id,
            color: visit.color,
            label: question.label,
            latitude: question.latitude,
            longitude: question.longitude,
            value: question.value,
            valueType: question.valueType
          });
        });

        return acc;
      }, []);
      previousMarker = [...previousMarker, ...reduced];
    });
    return previousMarker;
  }, [previousVisitsSelection]);

  const handleOnPreviousMarkerLocationClicked = (id: string) => {
    try {
      const markerInfo = previousVisitsMarkers.find(pvm => pvm.characterization_id === id);
      displayMarkerDetailsModal(markerInfo);
    } catch (error) {
      showBoundary(error);
    }
  };

  return {
    previousVisitsMarkers,
    handleOnPreviousMarkerLocationClicked
  };
}
